import { convertTimeToMMSS } from '@src/utility/timeFormat';

/**
 * PLAT-11532 / PLAT-11589 — why a dub cannot start on this much video, or
 * `undefined` when it can.
 *
 * Dub generation is one synchronous request with a fixed wall-clock budget, so
 * a long story times out part-way and nothing is kept. Rather than let a person
 * wait on a run that cannot finish, the surfaces that offer a dub disable it
 * over the cap and say so up front. The cap is LaunchDarkly-controlled
 * (`dubbingMaxMinutes`) because the honest number is still being measured.
 *
 * The note is split in two so the menu stays short (PLAT-11589): `line` is the
 * fact, painted once as a one-line warning under the disabled control;
 * `tooltip` is the way out, shown on hover of the control it explains.
 *
 * `story` is the Transcript & Speakers tab, which dubs the whole story and so
 * names it: the way out is a clip. `clip` is any surface measuring a PORTION —
 * Clip Preview, and Creator Studio, which gates on the timeline — so its line
 * states the length alone and names no subject.
 */
export interface DubDurationLimitNote {
  /** The fact, one line: "14:32 — over the 10:00 dub limit". */
  line: string;
  /** The way out, a sentence: "Trim this clip to 10:00 or less to dub it." */
  tooltip: string;
}

export function getDubDurationLimitNote(
  durationSec: number | null | undefined,
  maxMinutes: number,
  scope: 'story' | 'clip',
): DubDurationLimitNote | undefined {
  // A cap that is not a number is no cap: a NaN comparison here would disable
  // dubbing everywhere, which is worse than the timeout it guards against.
  if (!Number.isFinite(maxMinutes) || durationSec == null) return undefined;
  if (durationSec <= maxMinutes * 60) return undefined;
  const cap = convertTimeToMMSS(maxMinutes * 60);
  const length = convertTimeToMMSS(durationSec);
  return scope === 'story'
    ? {
        line: `Story is ${length} — over the ${cap} dub limit`,
        tooltip: 'Create a clip to dub part of this story.',
      }
    : {
        line: `${length} — over the ${cap} dub limit`,
        tooltip: `Trim this clip to ${cap} or less to dub it.`,
      };
}
